// ============================================================
//  SCAN-FAILED UPLOADS PAGE — ADMIN DASHBOARD
//  Path: src/pages/ScanFailedUploadsPage.tsx
//
//  Architecture doc section 14: after MAX_SCAN_ATTEMPTS unsuccessful
//  attempts, an UploadTicket is marked SCAN_FAILED (never REJECTED —
//  that means the FILE was unsafe; SCAN_FAILED means the scanner
//  itself couldn't finish, e.g. ClamAV unreachable, a storage
//  timeout). recovery.service.js's sendScanFailedDigest posts a daily
//  Slack alert naming these fileIds and says "re-queue from the admin
//  dashboard" — that page didn't exist until now. This is it.
//
//  A file listed here is not broken or unsafe; the platform just
//  hasn't been able to confirm it's safe yet. Re-queuing re-runs the
//  full check (size, real file-type, ClamAV, image re-encode or PDF
//  active-content scan) from scratch — it does not skip any step.
//
//  Access: super_admin, admin only — same level as verifying a
//  document (backend: admin.routes.js's /upload-tickets/scan-failed
//  and /upload-tickets/:fileId/rescan, both requireRole('super_admin',
//  'admin')). Not customer_service — re-queuing a stuck scan is an
//  operational action, not a view.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react'
import { listScanFailedUploadTickets, rescanUploadTicket } from '../services/admin.service'
import { ScanFailedUploadTicket } from '../types'

// Mirrors config/fileCategories.js's CATEGORIES keys — shown as-is
// (snake_case) if a new category is added here before this map is,
// so an unrecognised category never disappears from the table.
const CATEGORY_LABELS: Record<string, string> = {
  government_id:            'Government ID',
  selfie:                   'Selfie',
  background_check_report:  'Background Check Report',
  drivers_licence:          "Driver's Licence",
  vehicle_document:         'Vehicle Document',
  insurance_certificate:    'Insurance Certificate',
  company_registration:     'Company Registration',
  expert_document:          'Expert Document',
  task_attachment:          'Task Attachment',
  admin_case_document:      'Admin Case Document',
  avatar:                   'Avatar',
  ad_image:                 'Ad Image',
  ad_video:                 'Ad Video',
  category_icon:            'Category Icon',
}

const PAGE_SIZE = 20

const ScanFailedUploadsPage: React.FC = () => {
  const [tickets, setTickets]     = useState<ScanFailedUploadTicket[]>([])
  const [total, setTotal]         = useState(0)
  const [page, setPage]           = useState(1)
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState('')
  const [rescanningId, setRescanningId] = useState<string | null>(null)
  const [rescanResult, setRescanResult] = useState<{ id: string; outcome: string } | null>(null)
  // FIX: a failed list load also rendered "Nothing stuck right now —
  // every upload has either cleared or been rejected" under the error:
  // false reassurance on the one page meant to show stuck files.
  const [loadFailed, setLoadFailed] = useState(false)

  const fetchTickets = useCallback(async () => {
    setLoading(true)
    setError('')
    setLoadFailed(false)
    try {
      const res: any = await listScanFailedUploadTickets({ page, limit: PAGE_SIZE })
      const rows = res.data.data ?? []
      const newTotal = res.data.pagination?.total ?? 0
      // FIX: rescanning the last file on a later page left an empty page
      // saying "Nothing stuck" while earlier pages still had files.
      if (rows.length === 0 && page > 1 && newTotal > 0) {
        setPage((p) => Math.max(1, Math.min(p - 1, Math.ceil(newTotal / PAGE_SIZE))))
        return
      }
      setTickets(rows)
      setTotal(newTotal)
    } catch (err: any) {
      console.error('Failed to fetch scan-failed uploads:', err)
      setLoadFailed(true)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load scan-failed uploads.'))
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => { fetchTickets() }, [fetchTickets])

  const handleRescan = async (fileId: string) => {
    setRescanningId(fileId)
    setRescanResult(null)
    setError('')
    try {
      const res: any = await rescanUploadTicket(fileId)
      // Confirmed shape: admin.controller.js's rescanUploadTicket wraps
      // the scanner's own /rescan/:fileId response body — { success,
      // result: { outcome, ... } } — under res.data.data. outcome is
      // 'clean' | 'rejected' | 'requeued' | 'failed' | 'skipped'
      // (scanner.service.js's scanQuarantinedFile).
      const outcome = res?.data?.data?.result?.outcome ?? 'requeued'
      setRescanResult({ id: fileId, outcome })
      await fetchTickets()
    } catch (err: any) {
      console.error('Failed to rescan upload ticket:', err)
      // FIX: the backend waits for the scanner to finish, and the scanner
      // scales to zero — after a quiet period it needs 30-60s just to
      // start. The dashboard gives up after 20s (config/api.ts), so a
      // rescan that was working fine was reported as "Failed to
      // re-queue", and clicking again then hit "wrong status". A timeout
      // with no response now says it is probably still running, and the
      // list is re-checked once the scanner has had time to finish.
      const timedOut = err?.code === 'ECONNABORTED' || (err?.isNetworkError && !err?.response)
      if (timedOut) {
        setRescanResult({ id: fileId, outcome: 'pending' })
        setTimeout(() => { fetchTickets() }, 45_000)
      } else {
        setError(err?.response?.data?.message || `Failed to re-queue ${fileId} — try again.`)
      }
    } finally {
      setRescanningId(null)
    }
  }

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const OUTCOME_LABELS: Record<string, { label: string; className: string }> = {
    clean:    { label: 'Now clean — promoted successfully',        className: 'text-green-400' },
    rejected: { label: 'Rejected — the file itself failed a real check', className: 'text-red-400' },
    requeued: { label: 'Re-queued — will be tried again shortly',   className: 'text-blue-400' },
    failed:   { label: 'Failed again — still stuck, try later',     className: 'text-orange-400' },
    skipped:  { label: 'Already handled by something else',         className: 'text-gray-400' },
    pending:  { label: 'Still running — the scanner can take up to a minute to start. This list will refresh itself in about 45 seconds; please don’t click Rescan again before then.', className: 'text-blue-400' },
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Scan-Failed Uploads</h1>
        <p className="text-gray-400 text-sm mt-1">
          {loadFailed ? 'Files' : `${total} file${total === 1 ? '' : 's'}`} the scanner couldn't finish checking — not unsafe, just unconfirmed.
          Re-queuing re-runs the full check from scratch.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm flex items-center justify-between gap-3">
          {error}
          {loadFailed ? (
            <button onClick={() => fetchTickets()}
              className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
              Try again
            </button>
          ) : (
            <button onClick={() => setError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
          )}
        </div>
      )}

      {rescanResult && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4 text-sm flex items-center justify-between">
          <span className={OUTCOME_LABELS[rescanResult.outcome]?.className || 'text-gray-300'}>
            {rescanResult.id.slice(0, 12)}... — {OUTCOME_LABELS[rescanResult.outcome]?.label || rescanResult.outcome}
          </span>
          <button onClick={() => setRescanResult(null)} className="ml-3 text-gray-400 hover:text-white">✕</button>
        </div>
      )}

      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">File ID</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Category</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">File Name</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Attempts</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Uploaded</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Last Tried</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : loadFailed ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">—</td></tr>
            ) : tickets.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">
                Nothing stuck right now — every upload has either cleared or been rejected.
              </td></tr>
            ) : (
              tickets.map((t) => (
                <tr key={t.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-xs font-mono text-gray-400" title={t.id}>{t.id.slice(0, 12)}...</p>
                    <p className="text-xs text-gray-600">{t.ownerId.slice(0, 12)}...</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">
                    {CATEGORY_LABELS[t.category] || t.category}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300 max-w-xs truncate" title={t.fileName || undefined}>
                    {t.fileName || '—'}
                    {t.declaredMimeType && <span className="block text-xs text-gray-600">{t.declaredMimeType}</span>}
                  </td>
                  <td className="px-5 py-4">
                    <span className="px-2 py-1 text-xs rounded-full bg-orange-600/20 text-orange-400">
                      {t.scanAttempts} attempt{t.scanAttempts === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(t.createdAt)}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(t.scannedAt)}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => handleRescan(t.id)}
                      disabled={rescanningId === t.id}
                      className="px-3 py-1.5 text-xs bg-brand-500/10 text-brand-400 rounded-lg hover:bg-brand-500/20 disabled:opacity-50 transition-colors"
                    >
                      {rescanningId === t.id ? 'Re-queuing...' : 'Rescan'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
            <p className="text-sm text-gray-400">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ScanFailedUploadsPage